/**
 * The proposal's own small glyphs — lucide, at the stroke the boards draw.
 *
 * The app's `frame/icons.tsx` fixes one stroke for the whole product, which
 * is right for a toolbar and wrong here: the boards draw a tag's tick and plus
 * at 2.4 so they read at 12px, and every other glyph at 1.7. One file, so
 * the weights are decided once rather than per call site.
 */
import {
  Check, ChevronDown, ChevronRight, Clock, CreditCard, Eraser, X, ChevronUp, Download, FileText, Hand, Info, Lock, MessageSquare,
  Package, Pencil, Phone, Plus, ReceiptText, Repeat, Star, Type, User, Wrench,
  type LucideIcon,
} from 'lucide-react'

interface GlyphProps {
  size?: number
  className?: string
}

function glyph(Component: LucideIcon, strokeWidth: number) {
  return function ProposalGlyph({ size = 16, className }: GlyphProps) {
    return (
      <Component size={size} strokeWidth={strokeWidth} className={className}
        aria-hidden="true" focusable="false" />
    )
  }
}

/* Inside a 24px tag: heavier, so a 12px glyph does not dissolve. */
export const TagCheck = glyph(Check, 2.4)
export const TagPlus = glyph(Plus, 2.4)
export const TagPencil = glyph(Pencil, 2.4)
export const TagStar = glyph(Star, 2.2)

export const GlyphCheck = glyph(Check, 2)
export const GlyphChevronDown = glyph(ChevronDown, 2)
export const GlyphChevronUp = glyph(ChevronUp, 2)
export const GlyphChevronRight = glyph(ChevronRight, 2.2)
export const GlyphLock = glyph(Lock, 1.7)
export const GlyphLoop = glyph(Repeat, 1.7)
export const GlyphInfo = glyph(Info, 1.7)
export const GlyphPhone = glyph(Phone, 1.7)
export const GlyphChat = glyph(MessageSquare, 1.7)
export const GlyphType = glyph(Type, 1.7)
export const GlyphHand = glyph(Hand, 1.7)
export const GlyphFile = glyph(FileText, 1.7)
export const GlyphDownload = glyph(Download, 1.7)
export const GlyphPlus = glyph(Plus, 1.7)
export const GlyphEraser = glyph(Eraser, 1.7)
export const GlyphCard = glyph(CreditCard, 1.7)
export const GlyphClock = glyph(Clock, 2.2)
export const GlyphClose = glyph(X, 2.2)
export const GlyphCheckBold = glyph(Check, 2.6)

/** The four kinds of line, in §6.4's order. */
export const KindMaterial = glyph(Package, 1.7)
export const KindLabor = glyph(User, 1.7)
export const KindEquipment = glyph(Wrench, 1.7)
export const KindFee = glyph(ReceiptText, 1.7)
